import type { MiniId } from '../mini/core'
import { lazy, Suspense, useEffect } from 'react'
import { bootHold, bootShowing, bootStart } from '../lib/boot'
import { loadPage } from '../lib/fresh'
import { PageGuard } from './PageGuard'
import { useMe } from '../net/session'
import { useRoute, navigate } from './router'
import { Toaster } from '../ui/toast'
import { AskHost } from '../ui/Dialog'
import { useOverlays } from './overlays'
import { initAuth } from '../net/auth'
import { startOnline } from '../net/online'
import { Spinner } from '../ui/Icon'
import { Announcement } from '../ui/Announcement'
import { InvitePop } from '../ui/InvitePop'

const Home = lazy(loadPage(() => import('../screens/home/Home')))
const Setup = lazy(loadPage(() => import('../screens/setup/Setup')))
const GameScreen = lazy(loadPage(() => import('../screens/game/GameScreen')))
const Daily = lazy(loadPage(() => import('../screens/daily/Daily')))
const Boards = lazy(loadPage(() => import('../screens/boards/Boards')))
const Profile = lazy(loadPage(() => import('../screens/profile/Profile')))
const Settings = lazy(loadPage(() => import('../screens/settings/Settings')))
const Shop = lazy(loadPage(() => import('../screens/shop/Shop')))
const Premium = lazy(loadPage(() => import('../screens/premium/Premium')))
const Maps = lazy(loadPage(() => import('../screens/maps/Maps')))
const MapPage = lazy(loadPage(() => import('../screens/maps/MapPage')))
const MapBuilder = lazy(loadPage(() => import('../screens/maps/MapBuilder')))
const Servers = lazy(loadPage(() => import('../screens/servers/Servers')))
const MiniHome = lazy(loadPage(() => import('../mini/ui/MiniHome')))
const MiniRoom = lazy(loadPage(() => import('../mini/ui/MiniRoom')))
const MiniPlay = lazy(loadPage(() => import('../mini/ui/MiniPlay')))
const Room = lazy(loadPage(() => import('../screens/setup/Room')))
const Friends = lazy(loadPage(() => import('../screens/friends/Friends')))
const Admin = lazy(loadPage(() => import('../screens/admin/Admin')))
const Legal = lazy(loadPage(() => import('../screens/legal/Legal')))
const Reset = lazy(loadPage(() => import('../screens/auth/Reset')))
const Gift = lazy(loadPage(() => import('../screens/auth/Gift')))
const Invite = lazy(loadPage(() => import('../screens/auth/Invite')))
const Challenge = lazy(loadPage(() => import('../screens/challenge/Challenge')))
const NotFound = lazy(loadPage(() => import('../screens/NotFound')))
const SignupModal = lazy(loadPage(() => import('../screens/auth/SignupModal')))
const PremiumModal = lazy(loadPage(() => import('../screens/premium/PremiumModal')))
const DevViewer = lazy(loadPage(() => import('../screens/dev/DevViewer')))
const DevPlay = lazy(loadPage(() => import('../screens/dev/DevPlay')))

const TITLES: Partial<Record<string, string>> = {
  setup: 'Play', daily: "Today's daily", boards: 'Leaderboards', settings: 'Settings', shop: 'Shop', premium: 'Premium',
  maps: 'Custom maps', mapNew: 'Make a map', servers: 'Servers', friends: 'Friends', admin: 'Admin', legal: 'Legal',
}

let booted = false

export function App() {
  const route = useRoute()
  const overlays = useOverlays()
  useEffect(() => {
    if (booted) return
    booted = true
    void initAuth()
    startOnline()
  }, [])
  // the opening loader waits for the app to draw and for who you are (so the top bar doesn't change under you)
  const loaded = useMe((m) => m.loaded)
  useEffect(() => (loaded || !bootShowing() ? undefined : bootHold()), [loaded])
  useEffect(() => { bootStart() }, [])
  useEffect(() => {
    const t = TITLES[route.name]
    document.title = t ? `${t} · Pinnr` : 'Pinnr'
  }, [route.name])

  let page: React.ReactNode
  switch (route.name) {
    case 'home': page = <Home />; break
    case 'setup': page = <Setup kind={route.params.kind as 'solo' | 'duo' | 'multi'} />; break
    case 'room': page = <Room code={route.params.code} />; break
    case 'game': page = <GameScreen />; break
    case 'daily': page = <Daily />; break
    case 'boards': page = <Boards id={route.params.id} />; break
    case 'profile': page = <Profile username={route.params.username} />; break
    case 'settings': page = <Settings />; break
    case 'shop': page = <Shop />; break
    case 'premium': page = <Premium />; break
    case 'maps': page = <Maps />; break
    case 'map': page = <MapPage id={route.params.id} />; break
    case 'mapNew': page = <MapBuilder />; break
    case 'mapEdit': page = <MapBuilder id={route.params.id} />; break
    case 'servers': page = <Servers />; break
    case 'mini': page = <MiniHome game={route.params.game.toLowerCase() as MiniId} />; break
    case 'miniRoom': page = <MiniRoom game={route.params.game.toLowerCase() as MiniId} code={route.params.code} />; break
    case 'miniPlay': page = <MiniPlay game={route.params.game.toLowerCase() as MiniId} />; break
    case 'friends': page = <Friends />; break
    case 'admin': page = <Admin section={route.params.section} />; break
    case 'legal': page = <Legal doc={route.params.doc} />; break
    case 'reset': page = <Reset token={route.params.token} />; break
    case 'gift': page = <Gift code={route.params.code} />; break
    case 'invite': page = <Invite username={route.params.username} />; break
    case 'challenge': page = <Challenge id={route.params.id} />; break
    case 'dev':
      page = import.meta.env.DEV ? (route.params.tool === 'viewer' ? <DevViewer /> : <DevPlay />) : <NotFound />
      break
    default: page = <NotFound />
  }

  return (
    <>
      {route.name !== 'game' && route.name !== 'miniPlay' && <Announcement />}
      <PageGuard key={route.name + (route.params ? JSON.stringify(route.params) : '')}><Suspense fallback={<PageLoading />}>{page}</Suspense></PageGuard>
      <Suspense fallback={null}>
        {overlays.signup && <SignupModal reason={overlays.signup.reason} start={overlays.signup.mode} />}
        {overlays.premium && <PremiumModal reason={overlays.premium} />}
      </Suspense>
      <Toaster />
      <InvitePop />
      <AskHost />
    </>
  )
}

function PageLoading() {
  return (
    <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>
      <Spinner />
    </div>
  )
}

export function goHome() {
  void navigate('/')
}
